import { useState, useEffect, useMemo } from 'react'

// Dev console command declaration
declare global {
  interface Window {
    devSendBulkEmails?: () => Promise<void>
  }
}
import { useAuth } from '@/contexts/AuthContext'
import { useEarningsStore } from '@/stores/earningsStore'
import { usePeriodStore } from '@/stores/periodStore'
import { DriverMasterService } from '@/services/driverMasterService'
import { VehicleService } from '@/services/vehicleService'
import { CompensationService } from '@/services/compensationService'
import { TaxTableService } from '@/services/taxTableService'
import { DriverMaster } from '@/types/driverMaster'
import { Vehicle } from '@/types/vehicle'
import { Earning } from '@/types/earnings'
import { Compensation, getCompensationCategoryInfo } from '@/types/compensation'
import { Platform } from '@/types'
import { Button } from '@/components/ui/button'
import { Plus, Calculator, Download, Trash2, Edit, Search, Lock, LockOpen } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { DataTable } from '@/components/common/DataTable'
import { ManualEarningsDialog } from '@/components/earnings/ManualEarningsDialog'
import { ManualCompensationDialog } from '@/components/compensation/ManualCompensationDialog'
import { AddSingleCompensationDialog } from '@/components/compensation/AddSingleCompensationDialog'
import { ExportDialog } from '@/components/common/dialogs/ExportDialog'
import { ColumnVisibilityControl } from '@/components/ui/ColumnVisibilityControl'
import { Combobox } from '@/components/ui/combobox'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import {
  DEFAULT_SALARY_COLUMNS,
  getDefaultSalaryColumns,
  getVisibleSalaryColumns,
  SalaryReportRow,
  SalaryReportActions,
  STANDARD_SALARY_REPORT_TEMPLATES
} from '@/components/reports/reportConfig'
import {
  createSalaryReportData,
  filterSalaryData,
  sortSalaryData
} from '@/components/reports/salaryReportUtils'
import { formatSEK } from '@/lib/formatters'
import { PlatformBadge } from '@/components/ui/PlatformBadge'
import { toast } from 'sonner'
import { SalarySpecificationService } from '@/services/salarySpecificationService'
import { BulkSalarySpecificationService } from '@/services/bulkSalarySpecificationService'
import { sendBulkSalaryEmails, getBulkEmailPreview, BulkEmailResult } from '@/services/bulkEmailService'
import { useConfigStore } from '@/stores/configStore'
import { Progress } from '@/components/ui/progress'
import { SmartPeriodSelector } from '@/components/ui/SmartPeriodSelector'
import { functions, httpsCallable } from '@/lib/firebase'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { FeatureFlag } from '@/constants/featureFlags'
import { payoutCompanyService } from '@/services/payoutCompanyService'
import { PayoutCompany } from '@/types'

export function SalaryReportPage() {
  const { currentUser: user, loading: authLoading } = useAuth()
  const { earnings, loadAllEarnings, deleteEarning } = useEarningsStore()
  const { settings, loadSettings, loading: configLoading } = useConfigStore()
  const { lockPeriod, loadPeriods, getPeriodStatus, getPeriod } = usePeriodStore()
  const multiCompanyEnabled = useFeatureFlag(FeatureFlag.MULTI_COMPANY)

  // Payout companies
  const [payoutCompanies, setPayoutCompanies] = useState<PayoutCompany[]>([])

  // Component state
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const selectedPeriodObj = selectedPeriod ? getPeriod(selectedPeriod) : undefined
  const [reportData, setReportData] = useState<SalaryReportRow[]>([])
  const [driverMasters, setDriverMasters] = useState<DriverMaster[]>([])
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [compensations, setCompensations] = useState<Compensation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog state
  const [showManualDialog, setShowManualDialog] = useState(false)
  const [showCompensationDialog, setShowCompensationDialog] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [showAddCompensationDialog, setShowAddCompensationDialog] = useState(false)
  const [selectedRowForCompensation, setSelectedRowForCompensation] = useState<SalaryReportRow | null>(null)

  // Delete confirmation state
  const [deleteEarningId, setDeleteEarningId] = useState<string | null>(null)
  const [deleteCompensationId, setDeleteCompensationId] = useState<string | null>(null)

  // Edit state
  const [editingCompensation, setEditingCompensation] = useState<Compensation | null>(null)

  // Email state
  const [isSendingEmail, setIsSendingEmail] = useState(false)

  // Bulk email progress state
  const [emailProgress, setEmailProgress] = useState<{
    isOpen: boolean
    isSending: boolean
    current: number
    total: number
    result?: BulkEmailResult
  }>({ isOpen: false, isSending: false, current: 0, total: 0 })

  // Bulk email confirmation dialog state
  const [showBulkEmailConfirm, setShowBulkEmailConfirm] = useState(false)

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Column visibility — conditionally include payoutCompany column
  const defaultSalaryColumns = useMemo(
    () => getDefaultSalaryColumns(multiCompanyEnabled),
    [multiCompanyEnabled]
  )
  const { columns: columnConfigs, visibleColumns, updateColumnVisibility, resetToDefaults, applyTemplate } = useColumnVisibility({
    storageKey: 'salaryReportColumns',
    defaultColumns: defaultSalaryColumns
  })

  // Standard report template state
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null)

  // Identify all unique tax tables required for calculations
  const requiredTaxTables = useMemo(() => {
    const tables = new Set<number>()

    // Add global tax table if table method is used
    if (settings.taxCalculationMethod === 'table') {
      const globalTable = parseInt(settings.taxTableName?.replace(/\D/g, '') || '31')
      tables.add(globalTable)
    }

    // Add driver-specific tax tables
    driverMasters.forEach(driver => {
      if (driver.taxTableName) {
        const driverTable = parseInt(driver.taxTableName.replace(/\D/g, '')) || 31 // Strip "skattetabell" prefix from legacy data
        tables.add(driverTable)
      }
    })

    return Array.from(tables)
  }, [settings.taxCalculationMethod, settings.taxTableName, driverMasters])

  // Load initial data
  useEffect(() => {
    async function loadData() {
      // Wait for authentication to complete
      if (authLoading) {
        return
      }

      if (!user?.uid) {
        return
      }

      // Ensure settings are loaded first if not already loaded
      if (configLoading) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Load settings if they haven't been loaded yet (fallback)
        if (!settings || Object.keys(settings).length === 0) {
          await loadSettings(user)
        }

        // Load earnings, driver masters, vehicles, compensations, and periods in parallel
        await Promise.all([
          loadAllEarnings(user),
          loadDriverMasters(),
          loadVehicles(),
          loadCompensations(),
          loadPeriods(user)
        ])
      } catch (err) {
        console.error('Error loading data:', err)
        setError('Failed to load data. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user?.uid, authLoading, configLoading, loadAllEarnings, loadSettings, settings])

  // Load payout companies when flag is on
  useEffect(() => {
    if (!multiCompanyEnabled || !user) return
    payoutCompanyService.getPayoutCompanies(user.uid)
      .then(setPayoutCompanies)
      .catch(err => console.error('Failed to load payout companies:', err))
  }, [multiCompanyEnabled, user?.uid])

  // Load all required tax tables in parallel for salary calculations
  useEffect(() => {
    const loadAllTaxTables = async () => {
      if (requiredTaxTables.length === 0) return

      // Resolve the tax year for the currently selected period
      const payoutDate = selectedPeriodObj?.payoutDate?.toDate()
      const taxYear = selectedPeriod
        ? TaxTableService.getTaxYear(selectedPeriod, payoutDate)
        : new Date().getFullYear()

      try {
        // Load all tables that aren't already loaded for this year
        await Promise.all(
          requiredTaxTables.map(tableNum => {
            if (TaxTableService.isTableLoaded(tableNum, taxYear)) {
              return Promise.resolve()
            }
            console.log(`Loading tax table ${tableNum} (${taxYear})...`)
            return TaxTableService.loadTaxTable(tableNum, taxYear)
          })
        )
        console.log(`Successfully loaded ${requiredTaxTables.length} tax tables for ${taxYear}`)
      } catch (error) {
        console.error('Failed to load some tax tables:', error)
        setError('Fel vid laddning av skattetabeller')
      }
    }

    loadAllTaxTables()
  }, [requiredTaxTables, selectedPeriod, selectedPeriodObj])

  // Load driver masters
  const loadDriverMasters = async () => {
    if (!user) return

    try {
      const masters = await DriverMasterService.getDrivers(user)
      setDriverMasters(masters)
    } catch (err) {
      console.error('Error loading driver masters:', err)
      throw err
    }
  }

  // Load vehicles
  const loadVehicles = async () => {
    if (!user) return

    try {
      const vehicleList = await VehicleService.getVehicles(user)
      setVehicles(vehicleList)
    } catch (err) {
      console.error('Error loading vehicles:', err)
      throw err
    }
  }

  // Load compensations
  const loadCompensations = async () => {
    if (!user) return

    try {
      const comps = await CompensationService.getCompensations(user)
      setCompensations(comps)
    } catch (err) {
      console.error('Error loading compensations:', err)
      throw err
    }
  }


  // Get unique periods from earnings
  const availablePeriods = useMemo(() => {
    const periods = [...new Set(earnings.map(e => e.period).filter(Boolean))]
    return periods.sort((a, b) => b.localeCompare(a)) // Sort descending (newest first)
  }, [earnings])

  // Filter earnings by selected period
  const filteredEarnings = useMemo(() => {
    if (!selectedPeriod) return earnings
    return earnings.filter(earning => earning.period === selectedPeriod)
  }, [earnings, selectedPeriod])

  // Filter compensations by selected period
  const filteredCompensations = useMemo(() => {
    if (!selectedPeriod) return compensations
    return compensations.filter(comp => comp.period === selectedPeriod)
  }, [compensations, selectedPeriod])


  // Create report data when earnings, drivers, or compensations change
  useEffect(() => {
    function processData() {
      if (!user || filteredEarnings.length === 0 || driverMasters.length === 0) {
        setReportData([])
        return
      }

      // Resolve tax year for the selected period
      const payoutDate = selectedPeriodObj?.payoutDate?.toDate()
      const taxYear = selectedPeriod
        ? TaxTableService.getTaxYear(selectedPeriod, payoutDate)
        : new Date().getFullYear()

      // Check if all required tax tables are loaded before processing
      const allTablesLoaded = requiredTaxTables.every(tableNum =>
        TaxTableService.isTableLoaded(tableNum, taxYear)
      )

      if (!allTablesLoaded) {
        console.log('Waiting for all tax tables to load before processing salary data...')
        setReportData([]) // Clear data while waiting
        return
      }

      try {
        const data = createSalaryReportData(filteredEarnings, driverMasters, filteredCompensations, selectedPeriod, settings, payoutCompanies, payoutDate)
        setReportData(data)
      } catch (err) {
        console.error('Error processing salary data:', err)
        setError('Failed to calculate salary data')
      }
    }

    processData()
  }, [user, filteredEarnings, driverMasters, filteredCompensations, selectedPeriod, selectedPeriodObj, settings, requiredTaxTables])

  // Process data for display
  const filteredData = useMemo(() => {
    const filtered = filterSalaryData(reportData, searchTerm)
    // Sort by "Att utbetala" in descending order
    return sortSalaryData(filtered, 'amountToPay', 'desc')
  }, [reportData, searchTerm])

  // Handle row expansion
  const handleRowExpand = (rowId: string | number) => {
    const id = String(rowId)
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Get platform earnings for a specific driver
  const getDriverPlatformEarnings = (driverId: string): Earning[] => {
    return filteredEarnings.filter(earning => earning.driverId === driverId)
  }

  // Get compensations for a specific driver (using filtered compensations)
  const getDriverCompensations = (driverId: string): Compensation[] => {
    return filteredCompensations.filter(compensation => compensation.driverId === driverId)
  }

  // Handle earning deletion
  const handleDeleteEarning = async (earningId: string) => {
    if (!user) return

    try {
      await deleteEarning(user, earningId)
      setDeleteEarningId(null)

      // Reload data
      await Promise.all([
        loadAllEarnings(user),
        loadCompensations()
      ])
    } catch (error) {
      console.error('Error deleting earning:', error)
      toast.error('Det gick inte att ta bort intäktsposten')
    }
  }

  // Handle compensation deletion
  const handleDeleteCompensation = async (compensationId: string) => {
    if (!user) return

    try {
      await CompensationService.deleteCompensation(user, compensationId)
      setDeleteCompensationId(null)
      toast.success('Ersättning/avdrag borttaget')

      // Reload compensations
      await loadCompensations()
    } catch (error) {
      console.error('Error deleting compensation:', error)
      toast.error('Det gick inte att ta bort ersättningen/avdraget')
    }
  }

  // Handle compensation edit
  const handleEditCompensation = (compensation: Compensation) => {
    setEditingCompensation(compensation)
    setSelectedRowForCompensation(null) // Clear any previous row selection
    setShowAddCompensationDialog(true)
  }

  // Handle add compensation (new single compensation dialog)
  const handleAddCompensation = (row: SalaryReportRow) => {
    setEditingCompensation(null) // Clear any previous edit state
    setSelectedRowForCompensation(row)
    setShowAddCompensationDialog(true)
  }

  // Handle add compensation success (also used for edit)
  const handleAddCompensationSuccess = async () => {
    await loadCompensations()
    setShowAddCompensationDialog(false)
    setSelectedRowForCompensation(null)
    setEditingCompensation(null)
  }

  // Create expandable content for showing earnings by platform
  const createExpandableContent = (row: SalaryReportRow) => {

    const driverEarnings = getDriverPlatformEarnings(row.driverId)
    const driverCompensations = getDriverCompensations(row.driverId)

    if (driverEarnings.length === 0 && driverCompensations.length === 0) {
      return (
        <div className="text-muted-foreground text-sm">
          Inga intäkter eller ersättningar/avdrag registrerade för denna förare.
        </div>
      )
    }

    // Group earnings by platform
    const platformGroups = driverEarnings.reduce((acc, earning) => {
      const platform = earning.platform
      if (!acc[platform]) {
        acc[platform] = []
      }
      acc[platform].push(earning)
      return acc
    }, {} as Record<Platform, Earning[]>)

    return (
      <div className="space-y-4">
        {/* Earnings Section */}
        {driverEarnings.length > 0 && (
          <div>
            <div className="text-sm font-medium text-foreground mb-3">
              Intäkter per plattform ({driverEarnings.length} poster)
            </div>

            {Object.entries(platformGroups).map(([platform, platformEarnings]) => {
          const platformTotals = platformEarnings.reduce((totals, earning) => ({
            grossEarnings: totals.grossEarnings + earning.grossEarnings,
            netEarnings: totals.netEarnings + earning.netEarnings,
            tips: totals.tips + (earning.tips || 0)
          }), { grossEarnings: 0, netEarnings: 0, tips: 0 })

          return (
            <div key={platform} className="border border-border/50 rounded-lg p-2 bg-background">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <PlatformBadge platform={platform as Platform} />
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted-foreground">Bruttointäkter: <span className="font-medium text-foreground">{formatSEK(platformTotals.grossEarnings)}</span></span>
                    <span className="text-muted-foreground">Nettointäkter: <span className="font-medium text-foreground">{formatSEK(platformTotals.netEarnings)}</span></span>
                    <span className="text-muted-foreground">Dricks: <span className="font-medium text-foreground">{formatSEK(platformTotals.tips)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-muted-foreground">
                    {platformEarnings.length} {platformEarnings.length === 1 ? 'post' : 'poster'}
                  </div>
                  {/* Delete button for single earnings */}
                  {platformEarnings.length === 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => setDeleteEarningId(platformEarnings[0].id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              {platformEarnings.length > 1 && (
                <details className="mt-2">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                    Visa individuella poster ({platformEarnings.length})
                  </summary>
                  <div className="mt-1 space-y-1">
                    {platformEarnings.map((earning) => (
                      <div key={earning.id} className="text-xs p-1 bg-muted/50 rounded border">
                        <div className="flex justify-between items-center gap-3">
                          <div className="flex gap-3">
                            <span>Brutto: {formatSEK(earning.grossEarnings)}</span>
                            <span>Netto: {formatSEK(earning.netEarnings)}</span>
                            {earning.tips > 0 && <span>Dricks: {formatSEK(earning.tips)}</span>}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteEarningId(earning.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )
        })}
          </div>
        )}

        {/* Compensations and Deductions Section */}
        {driverCompensations.length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-medium text-foreground mb-2">
              Ersättningar och avdrag ({driverCompensations.length} poster)
            </div>

            <div className="border border-border/50 rounded-lg p-2 bg-background">
              {/* Group compensations by type */}
              {(() => {
                const compensationsByType = driverCompensations.reduce((acc, comp) => {
                  if (!acc[comp.type]) {
                    acc[comp.type] = []
                  }
                  acc[comp.type].push(comp)
                  return acc
                }, {} as Record<string, Compensation[]>)

                const compensations = compensationsByType['compensation'] || []
                const deductions = compensationsByType['deduction'] || []

                return (
                  <div className="space-y-2">
                    {/* Ersättningar */}
                    {compensations.length > 0 && (
                      <div>
                        <div className="font-medium text-xs text-green-600 mb-1">
                          Ersättningar ({compensations.length} poster)
                        </div>
                        <div className="space-y-1">
                          {compensations.map((comp) => {
                            const categoryInfo = getCompensationCategoryInfo(comp.category)
                            return (
                              <div key={comp.id} className="flex items-center justify-between text-xs p-1 bg-muted/50 rounded">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-green-600">+{formatSEK(comp.amount)}</span>
                                  <span className="text-muted-foreground">({categoryInfo.label})</span>
                                  <span className="text-muted-foreground">→ {
                                    comp.applyTo === 'net_earnings' ? 'Nettointäkter' :
                                    comp.applyTo === 'gross_salary' ? 'Bruttolön' :
                                    comp.applyTo === 'net_salary' ? 'Lön efter skatt' : 'Okänd'
                                  }</span>
                                  {comp.comment && (
                                    <span className="text-muted-foreground italic">"{comp.comment}"</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                    onClick={() => handleEditCompensation(comp)}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                    onClick={() => setDeleteCompensationId(comp.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Avdrag */}
                    {deductions.length > 0 && (
                      <div>
                        <div className="font-medium text-xs text-red-600 mb-1">
                          Avdrag ({deductions.length} poster)
                        </div>
                        <div className="space-y-1">
                          {deductions.map((comp) => {
                            const categoryInfo = getCompensationCategoryInfo(comp.category)
                            return (
                              <div key={comp.id} className="flex items-center justify-between text-xs p-1 bg-muted/50 rounded">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-red-600">-{formatSEK(comp.amount)}</span>
                                  <span className="text-muted-foreground">({categoryInfo.label})</span>
                                  <span className="text-muted-foreground">→ {
                                    comp.applyTo === 'net_earnings' ? 'Nettointäkter' :
                                    comp.applyTo === 'gross_salary' ? 'Bruttolön' :
                                    comp.applyTo === 'net_salary' ? 'Lön efter skatt' : 'Okänd'
                                  }</span>
                                  {comp.comment && (
                                    <span className="text-muted-foreground italic">"{comp.comment}"</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                    onClick={() => handleEditCompensation(comp)}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                    onClick={() => setDeleteCompensationId(comp.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Export functionality
  const handleExport = () => {
    setShowExportDialog(true)
  }

  // Generate salary specification PDF
  const handleGenerateSalarySpecification = async (row: SalaryReportRow) => {
    if (!user) {
      return
    }

    try {
      // Find the driver master
      const driver = driverMasters.find(d => d.id === row.driverId)
      if (!driver) {
        toast.error('Förare hittades inte')
        return
      }

      // Get the driver's earnings and compensations
      const driverEarnings = getDriverPlatformEarnings(row.driverId)
      const driverCompensations = getDriverCompensations(row.driverId)

      // Prepare data for PDF
      const specData = await SalarySpecificationService.prepareSalarySpecificationData(
        user,
        driver,
        selectedPeriod || '',
        driverEarnings,
        driverCompensations,
        row,
        settings
      )

      // Validate data
      const validation = SalarySpecificationService.validateSalarySpecificationData(specData)
      if (!validation.isValid) {
        toast.error(`Kan inte generera lönespecifikation: ${validation.errors.join(', ')}`)
        return
      }

      // Format period for display
      const formattedPeriod = selectedPeriod
        ? SalarySpecificationService.formatPeriodForDisplay(selectedPeriod)
        : 'Alla perioder'

      // Generate PDF blob using centralized service (checks for locked data first)
      const blob = await SalarySpecificationService.generatePdfBlob(user, specData, formattedPeriod)
      const filename = SalarySpecificationService.generateFilename(
        driver.primaryName,
        selectedPeriod || new Date().toISOString().slice(0, 10),
        multiCompanyEnabled ? specData.companyInfo?.name : undefined
      )

      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success('Lönespecifikation genererad')
    } catch (error) {
      console.error('Error generating salary specification:', error)
      toast.error('Fel vid generering av lönespecifikation')
    }
  }

  // Handle bulk PDF export
  const handleExportPdf = async () => {
    if (!user) {
      toast.error('Du måste vara inloggad')
      return
    }

    if (!selectedPeriod) {
      toast.error('Välj en period först')
      return
    }

    if (reportData.length === 0) {
      toast.error('Inga förare att exportera')
      return
    }

    try {
      const count = await BulkSalarySpecificationService.generateBulkPDFs(
        user,
        reportData,
        selectedPeriod,
        settings,
        earnings,
        compensations,
        driverMasters,
        multiCompanyEnabled
      )

      toast.success(`ZIP-fil med ${count} lönespecifikationer nedladdad`)
    } catch (error) {
      console.error('Bulk PDF generation failed:', error)
      toast.error('Något gick fel vid genereringen. Försök igen.')
    }
  }

  // Handle send salary specification email
  const handleSendSalaryEmail = async (row: SalaryReportRow) => {
    if (!user) {
      toast.error('Du måste vara inloggad')
      return
    }

    // Validate period is selected
    if (!selectedPeriod) {
      toast.error('Ingen period vald - kan inte skicka lönespecifikation')
      return
    }

    setIsSendingEmail(true)
    try {
      const sendEmail = httpsCallable<
        { driverId: string; periodName: string },
        { success: boolean; messageId?: string }
      >(functions, 'sendSalaryEmail')

      await sendEmail({
        driverId: row.driverId,
        periodName: selectedPeriod
      })

      // Find driver for success message (optional, backend handles validation)
      const driver = driverMasters.find(d => d.id === row.driverId)
      const driverEmail = driver?.email || 'föraren'

      toast.success(`Email skickat till ${driverEmail}`)
    } catch (error: any) {
      console.error('Error sending email:', error)
      const errorMessage = error?.message || 'Kunde inte skicka email'
      toast.error(errorMessage)
    } finally {
      setIsSendingEmail(false)
    }
  }

  // Email preview for bulk email
  const emailPreview = useMemo(() => {
    return getBulkEmailPreview(reportData, driverMasters)
  }, [reportData, driverMasters])

  // Memoize period lock status to avoid multiple calls
  const isLocked = useMemo(
    () => getPeriodStatus(selectedPeriod) === 'locked',
    [selectedPeriod, getPeriodStatus]
  )

  // Handle lock period only (without sending emails)
  const handleLockOnly = async () => {
    if (!user || !selectedPeriod) return
    try {
      await lockPeriod(user, selectedPeriod)
      setShowBulkEmailConfirm(false)  // Stäng dialog vid framgång (catch block förhindrar stängning vid fel)
    } catch (error) {
      // Store visar toast, dialogen förblir öppen
      console.error('Failed to lock period:', error)
    }
  }

  // Handle bulk email sending (locks period first)
  const handleBulkEmail = async () => {
    if (!user) {
      toast.error('Du måste vara inloggad')
      return
    }

    if (!selectedPeriod) {
      toast.error('Välj en period först')
      return
    }

    // Refresh period status to avoid stale state, then lock if needed
    await loadPeriods(user)
    if (getPeriodStatus(selectedPeriod) !== 'locked') {
      try {
        await lockPeriod(user, selectedPeriod)
      } catch (error) {
        console.error('Failed to lock period:', error)
        return
      }
    }

    if (emailPreview.withEmail === 0) {
      toast.error('Inga förare med email att skicka till')
      return
    }

    // Open progress dialog
    setEmailProgress({
      isOpen: true,
      isSending: true,
      current: 0,
      total: emailPreview.withEmail,
      result: undefined
    })

    try {
      const result = await sendBulkSalaryEmails(
        reportData,
        driverMasters,
        selectedPeriod,
        (current, total) => {
          setEmailProgress(prev => ({ ...prev, current, total }))
        }
      )

      // Show result
      setEmailProgress(prev => ({
        ...prev,
        isSending: false,
        result
      }))
    } catch (error) {
      console.error('Bulk email failed:', error)
      toast.error('Något gick fel vid email-utskick')
      setEmailProgress(prev => ({ ...prev, isOpen: false }))
    }
  }

  // Expose bulk email function for dev testing in console
  useEffect(() => {
    window.devSendBulkEmails = () => {
      setShowBulkEmailConfirm(true)
      return Promise.resolve()
    }
    return () => {
      delete window.devSendBulkEmails
    }
  }, [])

  // Handle standard report template selection from Combobox
  const handleTemplateChange = (templateId: string) => {
    const template = STANDARD_SALARY_REPORT_TEMPLATES.find(t => t.id === templateId)
    if (template) {
      applyTemplate(template.visibleColumns)
      setCurrentTemplateId(template.id)
      toast.success(`${template.name} vald`)
    }
  }

  // Get visible columns for the table (after handleGenerateSalarySpecification is defined)
  const displayColumns = useMemo(() => {
    const actions: SalaryReportActions = {
      onGenerateSalarySpecification: handleGenerateSalarySpecification,
      onSendEmail: handleSendSalaryEmail,
      onAddCompensation: handleAddCompensation
    }
    const visibleColumnKeys = visibleColumns.map(col => col.key)
    return getVisibleSalaryColumns(visibleColumnKeys, actions)
  }, [visibleColumns, driverMasters, selectedPeriod, isSendingEmail])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Calculator className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-spin" />
          <p className="text-muted-foreground">Laddar lönerapport...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Försök igen</Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Lönerapport</h1>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowManualDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Lägg till intäkter
            </Button>
            <Button onClick={() => setShowCompensationDialog(true)} className="gap-2">
              <Calculator className="h-4 w-4" />
              Lägg till ersättning/avdrag
            </Button>
            <Button onClick={handleExport} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Exportera
            </Button>
            <Button
              onClick={() => setShowBulkEmailConfirm(true)}
              variant="outline"
              className="gap-2"
              disabled={!selectedPeriod}
              aria-label={isLocked ? 'Period är låst - skicka lönespecifikationer' : 'Lås period'}
            >
              {isLocked ? (
                <Lock className="h-4 w-4 text-red-600" />
              ) : (
                <LockOpen className="h-4 w-4" />
              )}
              {isLocked ? 'Period låst' : 'Lås period'}
            </Button>
          </div>
        </div>

        <Separator />

        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 justify-between">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Sök förare..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SmartPeriodSelector
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              storageKey="salary-report-last-period"
              availablePeriods={availablePeriods.map(p => ({ value: p, label: p }))}
              className="w-full sm:w-auto"
            />
            <Combobox
              options={STANDARD_SALARY_REPORT_TEMPLATES.map(t => ({
                value: t.id,
                label: t.name
              }))}
              value={currentTemplateId || ''}
              onValueChange={handleTemplateChange}
              placeholder="Standardrapporter"
              searchPlaceholder="Sök rapport..."
              className="w-[200px]"
            />
            <ColumnVisibilityControl
              columns={columnConfigs}
              onVisibilityChange={updateColumnVisibility}
              onResetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        {/* Table */}
        {filteredData.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <Calculator className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Inga lönedata tillgängliga</p>
            <p className="text-muted-foreground mb-4">
              Ladda upp intäkter eller lägg till manuellt för att se löneberäkningar.
            </p>
            <Button onClick={() => setShowManualDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Lägg till intäkter
            </Button>
          </div>
        ) : (
          <DataTable
            data={filteredData}
            columns={displayColumns}
            expandableContent={createExpandableContent}
            expandedRows={expandedRows}
            onRowExpand={handleRowExpand}
            getRowId={(row) => row.driverId}
          />
        )}
        </div>

      <ManualEarningsDialog
        open={showManualDialog}
        onOpenChange={setShowManualDialog}
        drivers={driverMasters}
      />

      <ManualCompensationDialog
        open={showCompensationDialog}
        onOpenChange={setShowCompensationDialog}
        drivers={driverMasters}
        vehicles={vehicles}
        onSuccess={loadCompensations}
      />

      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        data={filteredData}
        columns={displayColumns}
        baseFilename="salary_report"
        title="Exportera lönerapport"
        onExportPdf={handleExportPdf}
      />

      <AddSingleCompensationDialog
        open={showAddCompensationDialog}
        onOpenChange={setShowAddCompensationDialog}
        compensation={editingCompensation}
        prefilledDriverId={selectedRowForCompensation?.driverId}
        prefilledPeriod={selectedPeriod}
        drivers={driverMasters}
        onSuccess={handleAddCompensationSuccess}
      />

      {/* Delete Earning Confirmation */}
      <AlertDialog open={deleteEarningId !== null} onOpenChange={() => setDeleteEarningId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort intäktspost</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort denna intäktspost? Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEarningId && handleDeleteEarning(deleteEarningId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Compensation Confirmation */}
      <AlertDialog open={deleteCompensationId !== null} onOpenChange={() => setDeleteCompensationId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort ersättning/avdrag</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort denna ersättning/avdrag? Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCompensationId && handleDeleteCompensation(deleteCompensationId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve Period Dialog */}
      <AlertDialog open={showBulkEmailConfirm} onOpenChange={setShowBulkEmailConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isLocked
                ? 'Skicka lönespecifikationer'
                : 'Godkänn löneperiod'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {isLocked ? (
                  <p className="mb-2">Perioden är redan låst.</p>
                ) : (
                  <p className="mb-2">Perioden låses och kan inte ändras efteråt.</p>
                )}
                {emailPreview.withEmail > 0 && (
                  <p className="text-muted-foreground text-sm">
                    {emailPreview.withEmail} förare kan få lönespecifikation via email.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            {!isLocked && (
              <Button variant="outline" onClick={handleLockOnly}>
                Lås period
              </Button>
            )}
            <AlertDialogAction
              onClick={() => {
                setShowBulkEmailConfirm(false)
                handleBulkEmail()
              }}
            >
              {isLocked ? 'Skicka lönespec' : 'Lås och skicka lönespec'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Email Progress Dialog */}
      <AlertDialog
        open={emailProgress.isOpen}
        onOpenChange={(open) => {
          if (!emailProgress.isSending) {
            setEmailProgress(prev => ({ ...prev, isOpen: open }))
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {emailProgress.isSending
                ? "Skickar lönespecifikationer..."
                : "Email skickade!"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {emailProgress.isSending ? (
                  <>
                    <p className="mb-4">
                      Vänligen stäng inte eller ladda om sidan medan email skickas.
                    </p>
                    <Progress
                      value={emailProgress.total > 0 ? (emailProgress.current / emailProgress.total) * 100 : 0}
                      className="mb-2"
                    />
                    <p className="text-center text-sm">
                      {emailProgress.current} / {emailProgress.total}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mb-2">
                      {emailProgress.result?.sent} av {emailProgress.total} email skickades.
                    </p>
                    {emailProgress.result && emailProgress.result.failed.length > 0 && (
                      <p className="text-destructive mb-2">
                        {emailProgress.result.failed.length} kunde inte skickas.
                      </p>
                    )}
                    {emailProgress.result && emailProgress.result.skipped > 0 && (
                      <p className="text-muted-foreground text-sm mb-2">
                        {emailProgress.result.skipped} förare hoppades över (saknar email).
                      </p>
                    )}
                    <p className="mt-4 text-xs text-muted-foreground">
                      Vid frågor, kontakta julius@baerell.com
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {!emailProgress.isSending && (
              <AlertDialogAction onClick={() => setEmailProgress(prev => ({ ...prev, isOpen: false }))}>
                Stäng
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}